/**
 * usePins — the only way feature code and the shell read/write pins.
 *
 * Reads the active user from `useAuth()` to namespace storage, subscribes to
 * the external store, and exposes a small, stable API. Mutations are plain
 * store functions (no need to thread them through deps).
 */
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useAuth } from '@shared/auth';
import { addPin, getSnapshot, removePin, setPinUser, subscribe, togglePin } from './store';
import {
  SECTION_FOR_KIND,
  type PinInput,
  type PinKind,
  type PinSection,
  type PinnedItem,
} from './types';

export type UsePinsResult = {
  /** All pins for the current user, newest-first. */
  pins: ReadonlyArray<PinnedItem>;
  isPinned: (kind: PinKind, id: string) => boolean;
  pin: (item: PinInput) => void;
  unpin: (kind: PinKind, id: string) => void;
  toggle: (item: PinInput) => void;
  /** Pins that belong in a given second-rail section. */
  pinsForSection: (section: PinSection) => PinnedItem[];
};

export function usePins(): UsePinsResult {
  const { user } = useAuth();
  const userKey = user?.id ?? user?.email ?? 'anon';

  // Point the store at this user before/at first paint.
  useEffect(() => {
    setPinUser(userKey);
  }, [userKey]);

  const pins = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return useMemo<UsePinsResult>(
    () => ({
      pins,
      isPinned: (kind, id) => pins.some((p) => p.kind === kind && p.id === id),
      pin: addPin,
      unpin: removePin,
      toggle: togglePin,
      pinsForSection: (section) => pins.filter((p) => SECTION_FOR_KIND[p.kind] === section),
    }),
    [pins]
  );
}
